// Admin Dashboard → A/B Testing: view and rotate the A/B Testing passcode that
// every A/B campaign create/edit/delete asks for (server: routes/api/ab_testing/
// ab_otp_routes.js, stored in profit_x_settings { type: "ab_testing_settings" }).
import React, { useCallback, useEffect, useState } from 'react';
import { Button, FormGroup, FormLabel, FormControl } from 'react-bootstrap';
import axios from 'axios';
import { showToast as toast } from '../../toastManager';

const PASSCODE_RE = /^\d{6}$/;

function randomPasscode() {
    const buf = new Uint32Array(1);
    window.crypto.getRandomValues(buf);
    return String(buf[0] % 1000000).padStart(6, '0');
}

export default function AbPasscodeManager({ app_url }) {
    const [settings, setSettings] = useState(null);
    const [loading, setLoading] = useState(true);
    const [reveal, setReveal] = useState(false);
    const [draft, setDraft] = useState('');
    const [saving, setSaving] = useState(false);

    const load = useCallback(async () => {
        setLoading(true);
        try {
            const { data } = await axios.get(`${app_url}/api/ab-testing/otp/settings`, { withCredentials: true });
            setSettings(data);
        } catch (err) {
            toast.error(err?.response?.data?.message || 'Could not load the A/B Testing passcode');
        } finally {
            setLoading(false);
        }
    }, [app_url]);

    useEffect(() => { load(); }, [load]);

    const save = async (otp) => {
        if (!PASSCODE_RE.test(otp)) {
            toast.error('The passcode must be exactly 6 digits.');
            return;
        }
        setSaving(true);
        try {
            const { data } = await axios.put(`${app_url}/api/ab-testing/otp/settings`, { otp }, { withCredentials: true });
            setSettings((prev) => ({ ...prev, ...data }));
            setDraft('');
            setReveal(true);
            toast.success('A/B Testing passcode updated');
        } catch (err) {
            toast.error(err?.response?.data?.message || 'Could not save the passcode');
        } finally {
            setSaving(false);
        }
    };

    const copy = () => {
        if (!settings?.otp) return;
        navigator.clipboard?.writeText(settings.otp).then(() => toast.success('Passcode copied')).catch(() => {});
    };

    return (
        <div className="admin-section">
            <div className="admin-section-header">
                <h3 className="admin-section-title">A/B Testing Passcode</h3>
                <p className="admin-section-description">
                    Every create, edit or delete of an A/B Testing campaign asks for this passcode. Share it only with
                    the people who should manage A/B tests. Which stores see A/B Testing at all is controlled by the
                    &ldquo;A/B Testing&rdquo; page access on the Accounts tab.
                </p>
            </div>

            {settings && settings.required === false && (
                <div className="ab-passcode-banner">
                    The passcode check is currently <strong>turned off</strong> on this server
                    (<code>AB_TESTING_OTP_DISABLED=true</code>). The code below is kept for when it is turned back on.
                </div>
            )}

            {loading ? (
                <p className="text-muted">Loading…</p>
            ) : (
                <div className="d-flex align-items-end gap-4 flex-wrap">
                    <FormGroup className="mb-0">
                        <FormLabel className="admin-form-label">Current passcode</FormLabel>
                        <div className="d-flex align-items-center gap-2">
                            <span className="ab-passcode-value" data-testid="ab-passcode-value">
                                {settings?.otp ? (reveal ? settings.otp : '••••••') : '—'}
                            </span>
                            <Button className="admin-btn" onClick={() => setReveal((v) => !v)} disabled={!settings?.otp}>
                                {reveal ? 'Hide' : 'Show'}
                            </Button>
                            <Button className="admin-btn" onClick={copy} disabled={!settings?.otp}>Copy</Button>
                        </div>
                        {settings?.updatedAt && (
                            <small className="text-muted d-block mt-1">
                                Last changed {new Date(settings.updatedAt).toLocaleString()}
                                {settings.updatedBy ? ` by ${settings.updatedBy}` : ''}
                            </small>
                        )}
                    </FormGroup>

                    <FormGroup className="mb-0" style={{ minWidth: '220px' }}>
                        <FormLabel className="admin-form-label">New passcode</FormLabel>
                        <FormControl
                            className="admin-form-control"
                            value={draft}
                            inputMode="numeric"
                            maxLength={6}
                            placeholder="6 digits"
                            onChange={(e) => setDraft(e.target.value.replace(/\D/g, '').slice(0, 6))}
                        />
                    </FormGroup>
                    <div className="d-flex gap-2">
                        <Button className="admin-btn" onClick={() => setDraft(randomPasscode())} disabled={saving}>Generate</Button>
                        <Button className="admin-btn admin-btn-primary" onClick={() => save(draft)} disabled={saving || !PASSCODE_RE.test(draft)}>
                            {saving ? 'Saving…' : 'Save passcode'}
                        </Button>
                    </div>
                </div>
            )}
        </div>
    );
}
